import React, { useState, useEffect } from "react"
import { Navigate } from "react-router-dom"
import axios from "axios"

import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { TOAST_SEVERITY_ERROR } from "../components/core/Toast/Toast"
import { useDispatch } from "react-redux"
import { updateToastData } from "../slices/toastSlice"
import Cookies from "js-cookie"

function sineWavePath(W, H, amp, phase, cycles, centerY) {
  const cy = centerY != null ? centerY : H / 2;
  const pts = [];
  for (let x = 0; x <= W; x += 3) {
    const t = (x / W) * cycles * 2 * Math.PI;
    const ampVar = 1 + 0.35 * Math.sin(t * 0.37 + phase * 1.7) + 0.2 * Math.sin(t * 0.71 + phase * 0.9) + 0.1 * Math.sin(t * 1.53 + phase * 2.3);
    const y = cy + amp * ampVar * Math.sin(phase + t);
    pts.push((x === 0 ? "M" : "L") + x + "," + y.toFixed(1));
  }
  return pts.join(" ");
}

const SignInPage = ({ isAuthenticated, setIsAuthenticated }) => {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [wavePhase, setWavePhase] = useState(0)
  const dispatch = useDispatch()

  useEffect(() => {
    let raf;
    const tick = () => { setWavePhase(p => p + 0.025); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [])

  if (localStorage.getItem("token")) {
    return <Navigate to={"/staff"} />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    Cookies.remove("refresh")
    let data = { username: username, password: password }
    axios
      .post("/api/token/", data)
      .then(async ({ data }) => {
        const { access } = data
        localStorage.setItem("token", access)
        setLoading(false)
        setIsAuthenticated(true)
      })
      .catch((e) => {
        Cookies.remove("refresh")
        localStorage.removeItem("token")
        dispatch(
          updateToastData({
            message: e,
            severity: TOAST_SEVERITY_ERROR,
          })
        )
        setLoading(false)
        setFailed(true)
      })
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#0d1b2a",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background sine waves */}
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }} viewBox="0 0 600 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="loginWave1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c8b99a" stopOpacity="0.06"/><stop offset="100%" stopColor="#c8b99a" stopOpacity="0"/></linearGradient>
          <linearGradient id="loginWave2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c8b99a" stopOpacity="0.04"/><stop offset="100%" stopColor="#c8b99a" stopOpacity="0"/></linearGradient>
        </defs>
        <path d={sineWavePath(600, 400, 20, -wavePhase * 0.5, 1.5, 320) + " L600,400 L0,400 Z"} fill="url(#loginWave1)"/>
        <path d={sineWavePath(600, 400, 15, -wavePhase * 0.3 + 1, 2, 350) + " L600,400 L0,400 Z"} fill="url(#loginWave2)"/>
        <path d={sineWavePath(600, 400, 20, -wavePhase * 0.5, 1.5, 320)} fill="none" stroke="#c8b99a" strokeWidth="0.8" opacity="0.15"/>
        <path d={sineWavePath(600, 400, 15, -wavePhase * 0.3 + 1, 2, 350)} fill="none" stroke="#c8b99a" strokeWidth="0.6" opacity="0.1"/>
      </svg>

      {/* Logo */}
      <Box sx={{ mb: 4, textAlign: "center", position: "relative" }}>
        <img
          src="https://marlinsurf-assets.s3.us-west-1.amazonaws.com/marlin-logo-transparent.png"
          alt="Marlin Surfboards"
          style={{ height: 64, marginBottom: 12, filter: "brightness(0.9)" }}
          onError={(e) => { e.target.style.display = "none" }}
        />
        <Typography
          sx={{
            fontFamily: "'Kenilla', 'Playfair Display', serif",
            fontSize: "1.8rem",
            color: "#f5f2ec",
            letterSpacing: "0.05em",
          }}
        >
          Marlin Surfboards
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: "0.7rem",
            color: "#c8b99a",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            mt: 0.5,
          }}
        >
          Staff Portal
        </Typography>
      </Box>

      {/* Login card */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 380,
          backgroundColor: "#152737",
          border: "1px solid rgba(200,185,154,0.12)",
          borderRadius: 2,
          p: 4,
          position: "relative",
        }}
      >
        <TextField
          margin="normal"
          required
          fullWidth
          id="username"
          label="Username"
          name="username"
          autoComplete="username"
          autoFocus
          disabled={loading}
          error={failed}
          onChange={(e) => setUsername(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(13,27,42,0.5)",
              fontFamily: "'Josefin Sans', sans-serif",
              color: "#f5f2ec",
              "& fieldset": { borderColor: "rgba(200,185,154,0.2)" },
              "&:hover fieldset": { borderColor: "#c8b99a" },
              "&.Mui-focused fieldset": { borderColor: "#c8b99a" },
            },
            "& .MuiInputLabel-root": { color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#c8b99a" },
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          disabled={loading}
          error={failed}
          onChange={(e) => setPassword(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(13,27,42,0.5)",
              fontFamily: "'Josefin Sans', sans-serif",
              color: "#f5f2ec",
              "& fieldset": { borderColor: "rgba(200,185,154,0.2)" },
              "&:hover fieldset": { borderColor: "#c8b99a" },
              "&.Mui-focused fieldset": { borderColor: "#c8b99a" },
            },
            "& .MuiInputLabel-root": { color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#c8b99a" },
          }}
        />
        {failed && (
          <Typography sx={{ color: "#ea0606", fontSize: "0.8rem", fontFamily: "'Josefin Sans', sans-serif", mt: 1 }}>
            Invalid credentials. Please try again.
          </Typography>
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            mt: 3,
            py: 1.5,
            backgroundColor: "#c8b99a",
            color: "#0d1b2a",
            fontFamily: "'Josefin Sans', sans-serif",
            fontWeight: 600,
            fontSize: "0.8rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            borderRadius: 0,
            "&:hover": { backgroundColor: "#d4c7a8" },
            "&.Mui-disabled": {
              backgroundColor: "rgba(200,185,154,0.3)",
              color: "rgba(13,27,42,0.5)",
            },
          }}
        >
          Sign In
        </Button>
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          mt: 4,
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: "0.65rem",
          color: "#5a6a7a",
          letterSpacing: "0.1em",
          position: "relative",
        }}
      >
        © {new Date().getFullYear()} Marlin Surfboards
      </Typography>
    </Box>
  )
}

export default SignInPage
